import React from 'react';
import { PROPERTY_TYPES } from '../../../constants/propertyConstants';

const Step1BasicInfo = ({ formData, onChange, errors }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-soil-900 mb-4">Basic Information</h3>
                <p className="text-sm text-soil-600 mb-6">
                    Let's start with the essential details about your property
                </p>
            </div>

            {/* Property Name */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Property Name <span className="text-murram-500">*</span>
                </label>
                <input
                    type="text"
                    value={formData.propertyName || ''}
                    onChange={(e) => onChange('propertyName', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.propertyName ? 'border-murram-500' : 'border-soil-100'
                    }`}
                    placeholder="e.g., Luxury 2-Bedroom Apartment in Westlands"
                />
                {errors.propertyName && (
                    <p className="mt-1 text-sm text-murram-600">{errors.propertyName}</p>
                )}
            </div>

            {/* Property Type */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Property Type <span className="text-murram-500">*</span>
                </label>
                <select
                    value={formData.propertyTypeId || ''}
                    onChange={(e) => onChange('propertyTypeId', parseInt(e.target.value))}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.propertyTypeId ? 'border-murram-500' : 'border-soil-100'
                    }`}
                >
                    <option value="">Select property type</option>
                    {PROPERTY_TYPES.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.name} - {type.description}
                        </option>
                    ))}
                </select>
                {errors.propertyTypeId && (
                    <p className="mt-1 text-sm text-murram-600">{errors.propertyTypeId}</p>
                )}
            </div>

            {/* Address Line 1 */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Address Line 1 <span className="text-murram-500">*</span>
                </label>
                <input
                    type="text"
                    value={formData.addressLine1 || ''}
                    onChange={(e) => onChange('addressLine1', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.addressLine1 ? 'border-murram-500' : 'border-soil-100'
                    }`}
                    placeholder="e.g., 123 Riverside Drive"
                />
                {errors.addressLine1 && (
                    <p className="mt-1 text-sm text-murram-600">{errors.addressLine1}</p>
                )}
            </div>

            {/* Address Line 2 */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Address Line 2 <span className="text-soil-400">(Optional)</span>
                </label>
                <input
                    type="text"
                    value={formData.addressLine2 || ''}
                    onChange={(e) => onChange('addressLine2', e.target.value)}
                    className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                    placeholder="e.g., Apartment 4B, Building C"
                />
            </div>

            {/* City, State, Country Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        City <span className="text-murram-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.city || ''}
                        onChange={(e) => onChange('city', e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                            errors.city ? 'border-murram-500' : 'border-soil-100'
                        }`}
                        placeholder="Nairobi"
                    />
                    {errors.city && (
                        <p className="mt-1 text-sm text-murram-600">{errors.city}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        State/Province
                    </label>
                    <input
                        type="text"
                        value={formData.stateProvince || ''}
                        onChange={(e) => onChange('stateProvince', e.target.value)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="Nairobi County"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Country
                    </label>
                    <input
                        type="text"
                        value={formData.country || ''}
                        onChange={(e) => onChange('country', e.target.value)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="Kenya"
                    />
                </div>
            </div>

            {/* Postal Code & Neighborhood */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Postal Code
                    </label>
                    <input
                        type="text"
                        value={formData.postalCode || ''}
                        onChange={(e) => onChange('postalCode', e.target.value)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="00100"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Neighborhood
                    </label>
                    <input
                        type="text"
                        value={formData.neighborhood || ''}
                        onChange={(e) => onChange('neighborhood', e.target.value)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="Westlands"
                    />
                </div>
            </div>

            {/* Latitude & Longitude */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Latitude <span className="text-soil-400">(Optional)</span>
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={formData.latitude || ''}
                        onChange={(e) => onChange('latitude', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="-1.286389"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Longitude <span className="text-soil-400">(Optional)</span>
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={formData.longitude || ''}
                        onChange={(e) => onChange('longitude', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        placeholder="36.817223"
                    />
                </div>
            </div>

            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4">
                <p className="text-sm text-highland-600">
                    <strong>Tip:</strong> Providing latitude and longitude helps display your property accurately on maps.
                </p>
            </div>
        </div>
    );
};

export default Step1BasicInfo;
